import type { Unformatted } from '../../utils/value.js'
import type * as Style from '../index.js'
import * as Ruleset from '../methods/set.js'
import { Gradient } from './gradient.js'
import { Float, Number } from './number.js'

export interface ColorDefinition {
  color?: ColorRGB
}

export const ColorDefinition = (props: any = {}) => ({
  color: ColorRGB(props.color)
})

export const ColorRGBnormalize = (props: any = {}) => ({
  red: Math.max(0, Math.min(255, Number(props.red) ?? 0)),
  green: Math.max(0, Math.min(255, Number(props.green) ?? 0)),
  blue: Math.max(0, Math.min(255, Number(props.blue) ?? 0)),
  alpha: Math.max(0, Math.min(1, Float(props.alpha) ?? 0))
})
export type ColorRGB = ReturnType<typeof ColorRGBnormalize>
export const ColorRGB = (props: Unformatted<ColorRGB>) => ColorRGBnormalize(props)

ColorRGB.isValid = (color: ColorRGB) => {
  return (
    color &&
    color.red >= 0 &&
    color.red <= 255 &&
    color.green >= 0 &&
    color.green <= 255 &&
    color.blue >= 0 &&
    color.blue <= 255 &&
    color.alpha >= 0 &&
    color.alpha <= 1
  )
}

export const rgbaToString = (color: ColorRGB) => {
  if (!color) return

  return `rgba(${color.red}, ${color.green}, ${color.blue}, ${color.alpha})`
}

export function isColorReference(color: any): color is ColorReference {
  return color && color.hasOwnProperty('id') && color.hasOwnProperty('alpha')
}

export function isColor(color: any) {
  return color && color.hasOwnProperty('red') && color.hasOwnProperty('green') && color.hasOwnProperty('blue')
}

export const gradientToBackgroundColor = (backgroundGradient: Gradient) =>
  backgroundGradient && backgroundGradient.stops.length > 0 ? backgroundGradient.stops[0].color : emptyColorReference

export const backgroundColorToGradient = (backgroundColor: ColorRGB | ColorReference): Gradient => {
  if (backgroundColor) {
    return {
      stops: [
        { color: backgroundColor, start: null, end: null },
        { color: backgroundColor, start: null, end: null }
      ],
      angle: 0
    }
  }

  return {
    stops: [],
    angle: 0
  }
}

export const emptyColorReference: ColorReference = { id: null, alpha: null }

export const findColor = (
  colorReference: ColorReference,
  colors: Style.Rule<'color'>[]
): { colorGroup: Style.Rule<'color'>; colorDefinition: ColorDefinition } => {
  const color = colors.find((stylesColor: Style.Rule<'color'>) => stylesColor.details.id === colorReference.id)

  if (!color) return { colorGroup: null, colorDefinition: null }
}

export const findAlpha = (colorReference: ColorReference, colors: Style.Rule<'color'>[]) => {
  if (!colorReference) return 0

  if (colorReference.alpha != null) {
    return colorReference.alpha
  }

  for (const c of colors) {
    if (c.details.id === colorReference.id) {
      return c.props.alpha
    }
  }

  return 0
}

export interface ColorReference {
  alpha: number | null
  id: Style.RuleId
}

export const ColorReference = (color?: any): ColorReference => {
  if (!color) {
    return {
      id: null,
      alpha: null
    }
  }

  // Fixme: ColorReference accepting Colors is not type-safe concept.
  // Need to figure out how to deal with it another way.
  if (ColorRGB.isValid(color as any)) {
    return color
  }

  let id = color.id || null
  let alpha = color.alpha ?? null

  return {
    id,
    alpha
  }
}

ColorReference.isValid = (color: ColorReference | ColorRGB) => {
  if (isColorReference(color)) {
    return color && color.id != null
  } else if (isColor(color)) {
    return ColorRGB.isValid(color as any)
  }
}

export const areSameColors = (firstColor: ColorReference, secondColor: ColorReference) =>
  firstColor?.id === secondColor?.id
